import React, { useEffect, useRef } from 'react';
import { Brain, Headphones, Rocket, Shield, TrendingUp, Users2 } from 'lucide-react';
import { siteContent } from '../content/siteContent';

const reasonIconMap = {
  brain: Brain,
  trendingUp: TrendingUp,
  rocket: Rocket,
  users2: Users2,
  shield: Shield,
  headphones: Headphones,
};

const WhyDotCreate = () => {
  const sectionRef = useRef(null);
  const { whyDotCreate } = siteContent;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.fade-in-up');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      <div className="absolute top-0 left-1/2 w-96 h-96 bg-[#88171A] rounded-full filter blur-3xl opacity-10"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-20">
          <div className="fade-in-up inline-block px-4 py-2 rounded-full bg-[#D22527]/10 border border-[#D22527]/30 text-[#D22527] text-sm font-semibold mb-6">
            {whyDotCreate.badge}
          </div>
          <h2 className="fade-in-up text-5xl md:text-6xl font-bold text-white mb-6">
            {whyDotCreate.heading[0]}{' '}
            <span className="bg-gradient-to-r from-[#D22527] to-[#88171A] bg-clip-text text-transparent">
              {whyDotCreate.heading[1]}
            </span>
          </h2>
          <p className="fade-in-up text-xl md:text-2xl text-gray-400 font-light max-w-4xl mx-auto">
            {whyDotCreate.intro}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {whyDotCreate.reasons.map((reason, index) => {
            const Icon = reasonIconMap[reason.icon];
            return (
              <div
                key={index}
                className="fade-in-up group p-8 rounded-3xl bg-gradient-to-br from-[#D22527]/5 to-transparent border border-[#D22527]/20 hover:border-[#D22527]/50 transition-all duration-500 hover:scale-105 backdrop-blur-sm"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#D22527] to-[#88171A] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 group-hover:rotate-6">
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#D22527] transition-colors duration-300">
                  {reason.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">{reason.description}</p>
              </div>
            );
          })}
        </div>

        <div className="fade-in-up text-center p-12 rounded-3xl bg-gradient-to-br from-[#D22527]/10 to-transparent border border-[#D22527]/30 backdrop-blur-sm">
          <p className="text-sm uppercase tracking-[0.25em] text-[#D22527] mb-4">{whyDotCreate.positioningLabel}</p>
          <p className="text-3xl md:text-4xl text-white font-light leading-relaxed">
            {whyDotCreate.positioningLead}
            <br />
            <span className="font-bold bg-gradient-to-r from-[#D22527] to-[#88171A] bg-clip-text text-transparent">
              {whyDotCreate.positioningAccent}
            </span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default WhyDotCreate;
